/*
 * <launch-countdown> — ticking countdown to the theme-wide launch deadline.
 *
 * The element upgrades over a server-rendered sentence ("Unveiling 15
 * September, 15:00"). Nothing here runs until then, which is the point: pages
 * are served from the page cache, so digits rendered by Liquid would ship
 * stale and sit frozen. Anything that stops this script — a cache miss on the
 * asset, an unparseable deadline, JS off — leaves that sentence in place.
 *
 * Standalone: no dependency on global.js, since the announcement bar and the
 * landing page both render it on templates that load their own assets.
 *
 * Wrapped in an IIFE because of that last point: a page carrying two hosts (the
 * landing page renders the bar as well as its own) emits this tag twice, and the
 * browser runs it once per tag. At file scope the second run throws on the
 * duplicate `class`/`const` declaration before the guard below is ever reached.
 */

(() => {
  /*
   * How long the zero state stands after the deadline. A launch that slips must
   * not leave "Unveiling today" on the site for days, and a page left open over
   * the window has to take itself down without a reload. One day covers every
   * market's clock, so the day itself still reads "today" everywhere.
   */
  const GRACE_MS = 24 * 60 * 60 * 1000;

  class LaunchCountdown extends HTMLElement {
    /*
     * Milliseconds remaining to whole units. Hours are uncapped rather than
     * carried into a days column: every placement reads one inline line, so a
     * two-day run reads "47H 59M 59S" instead of opening a second group.
     */
    static split(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));

      return {
        total,
        hours: Math.floor(total / 3600),
        minutes: Math.floor(total / 60) % 60,
        seconds: total % 60,
      };
    }

    /*
     * The one decision every entry point shares: keep counting, show the zero
     * state, or take the element off the page. Reads whole seconds through
     * `split` so the counting/done boundary is the one a tick actually sees.
     */
    static state(ms) {
      if (ms < -GRACE_MS) return 'hide';

      return LaunchCountdown.split(ms).total > 0 ? 'counting' : 'done';
    }

    static pad(value) {
      return String(value).padStart(2, '0');
    }

    /*
     * Hours are unpadded and uncapped; minutes and seconds keep their pad, so
     * only the hours group changes width across a run. `units` is the
     * caller's per-group letter (read from the locale, never hardcoded here).
     */
    static formatClock(parts, units) {
      return [
        `${parts.hours}${units.hours}`,
        `${LaunchCountdown.pad(parts.minutes)}${units.minutes}`,
        `${LaunchCountdown.pad(parts.seconds)}${units.seconds}`,
      ].join(' ');
    }

    connectedCallback() {
      const deadline = Date.parse(this.dataset.deadline);
      if (!Number.isFinite(deadline)) return;

      this.deadline = deadline;

      const state = LaunchCountdown.state(deadline - Date.now());
      if (state === 'hide') {
        this.expire();
        return;
      }

      /*
       * Re-entering after the deadline goes straight to the zero state. `finish`
       * drops the counting nodes, so rebuilding them here would flash digits for
       * a frame before the first tick swept them away again.
       */
      if (state === 'done' || this.done) {
        this.finish();
        return;
      }

      if (!this.clock) this.build();
      this.tick();
      if (!this.timer) this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    build() {
      const label = document.createElement('span');
      label.className = 'launch-countdown__label';
      label.textContent = this.dataset.label || '';

      this.units = {
        hours: this.dataset.unitHours || '',
        minutes: this.dataset.unitMinutes || '',
        seconds: this.dataset.unitSeconds || '',
      };

      this.clock = document.createElement('span');
      this.clock.className = 'launch-countdown__clock';

      /*
       * A real space between the two spans, not just the CSS flex gap: the gap
       * is invisible to `textContent`, so a screen reader (or anything else
       * that reads the flattened text) ran the label straight into the clock —
       * "Unveiling in22H 27M 07S". A whitespace-only text node isn't rendered
       * as a flex item, so it adds nothing visually alongside the existing gap.
       */
      this.textContent = '';
      this.append(label, ' ', this.clock);
    }

    tick() {
      const remaining = this.deadline - Date.now();
      const state = LaunchCountdown.state(remaining);

      if (state === 'hide') {
        this.expire();
        return;
      }

      if (state === 'done') {
        this.finish();
        return;
      }

      this.clock.textContent = LaunchCountdown.formatClock(LaunchCountdown.split(remaining), this.units);
    }

    /*
     * Zero state. The deadline can pass while the launch itself is late, so this
     * has to stand on its own: it stops ticking and states the day, and the host
     * surfaces keep whatever link they already had.
     */
    finish() {
      window.clearInterval(this.timer);
      this.timer = null;
      this.clock = null;
      this.done = true;

      const done = document.createElement('span');
      done.className = 'launch-countdown__label launch-countdown__label--done';
      done.textContent = this.dataset.done || '';

      /* Live before the text lands: a region switched on after the fact has
       * nothing to announce. */
      this.setAttribute('aria-live', 'polite');
      this.textContent = '';
      this.appendChild(done);
    }

    /* Past the grace window: the element leaves rather than restate a day that
     * has been and gone. */
    expire() {
      window.clearInterval(this.timer);
      this.timer = null;

      /* A host that joined this element onto its own text marks how much of the
       * line goes with it — otherwise the announcement bar keeps "HÖGALID ·"
       * with nothing after the glyph. */
      (this.closest('[data-launch-countdown-scope]') || this).remove();
    }
  }

  if (!customElements.get('launch-countdown')) {
    customElements.define('launch-countdown', LaunchCountdown);
  }
})();
